import * as amplitude from '@amplitude/analytics-browser';
import { useState } from 'react';
import Truncate from 'react-truncate';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { FakeLink } from '@hivebrite/design-system/Link';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import ReactionsSharesDropdown from 'hb-react/shared/components/DesignSystem/Reactions/ReactionsShares/ReactionsSharesDropdown';
import FolderDetailsModal from 'hb-react/shared/components/FolderPageHeader/FolderDetailsModal';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import { useFeature } from 'hb-react/shared/hooks/useFeature';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';
import IconShare from 'hb-react/shared/icons/Share';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const FolderPageHeader = ({
  folder,
  centerButton: CenterButton,
  rightButton: RightButton,
  shareToLiveFeedEnabled,
  shareToLiveFeedUrl,
  sharedFrom,
  sharedUrl,
}) => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const breakpoint = useBreakpoint();
  const {
    cover_picture_file_url: pictureUrl,
    description,
    name,
    total_items: totalItems,
  } = folder;

  const { feature } = useFeature('media_center');

  // This setting is labeled "Enable likes, comments, and native sharing (when applicable)" in the Media Center settings
  const nativeSharingEnabled = feature?.config?.comments_enabled;

  const [modalOpen, setModalOpen] = useState(false);
  const [hasLongDescription, setHasLongDescription] = useState(false);

  const { handleShare: handleShareButton, shareUrl } = useNavigatorShare({
    data: {
      text: name,
      url: window.document.URL,
    },
    onShare: () => {
      amplitude.track('MediaCenter|FolderShared');
    },
  });

  return (
    <MainWrapper>
      {breakpoint === 's' ? (
        <Box
          justifyContent={pictureUrl ? 'space-between' : 'flex-end'}
          width="100%"
          alignItems="start"
          mb={3}
        >
          {pictureUrl && <Picture $source={pictureUrl} />}
          {RightButton && <RightButton />}
        </Box>
      ) : (
        pictureUrl && (
          <WrapperPicture>
            <Picture $source={pictureUrl} />
          </WrapperPicture>
        )
      )}
      <ContentWrapper hasPicture={pictureUrl}>
        <ThemeProvider
          theme={outboxedFontTheme.themeName}
          fontBrightness={outboxedFontTheme.fontBrightness}
        >
          <WrapperTitleAndDescription>
            <Title>{`${name} (${totalItems})`}</Title>
            <Description>
              <Truncate
                lines={2}
                onTruncate={(isTruncated) => {
                  if (isTruncated) {
                    setHasLongDescription(isTruncated);
                  }
                }}
              >
                {description}
              </Truncate>
              {hasLongDescription && (
                <FakeLink onClick={() => setModalOpen(true)}>
                  {__('See more')}
                </FakeLink>
              )}
            </Description>
          </WrapperTitleAndDescription>
        </ThemeProvider>
        <Box alignItems="center">
          <ReactionsSharesDropdown
            content={folder}
            onShareExternally={
              nativeSharingEnabled ? handleShareButton : undefined
            }
            shareToLiveFeedEnabled={shareToLiveFeedEnabled}
            shareToLiveFeedUrl={shareToLiveFeedUrl}
            shareUrl={shareUrl}
            sharedFrom={sharedFrom}
            sharedUrl={sharedUrl}
          >
            {({ onClick, ...rest }) => (
              <Button onClick={onClick} icon={IconShare} mr={3} {...rest}>
                {__('Share folder')}
              </Button>
            )}
          </ReactionsSharesDropdown>
          {CenterButton && (
            <Box display="block">
              <CenterButton folder={folder} />
            </Box>
          )}
        </Box>
      </ContentWrapper>
      {breakpoint !== 's' && RightButton && <RightButton />}
      {modalOpen && (
        <FolderDetailsModal
          name={name}
          description={description}
          coverPictureFileUrl={pictureUrl}
          onCloseModal={() => setModalOpen(false)}
          title={s__('MediaCenter|%{name} (%{totalItems})').i__({
            totalItems,
            name,
          })}
          ariaLabel={s__('MediaCenter|Cover picture of %{folderName}').i__({
            folderName: name,
          })}
        />
      )}
    </MainWrapper>
  );
};

export default FolderPageHeader;

const MainWrapper = styled(Box).attrs({
  display: 'flex',
  flexDirection: { _: 'column', m: 'row' },
  alignItems: 'start',
  flex: 1,
})``;

const Picture = styled(Box).attrs(({ $source }) => ({
  width: { _: 162, m: '100%' },
  height: { _: $source ? 91 : 'auto', m: '100%' },
}))`
  border-radius: 3px;
  ${({ $source }) =>
    $source &&
    css`
      background-image: url('${$source}');
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    `}
`;

const ContentWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['hasPicture'].includes(prop),
  })
  .attrs(({ hasPicture }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    ml: hasPicture && { _: 0, m: 5, l: 6 },
  }))``;

const WrapperTitleAndDescription = styled(Box).attrs({
  width: { _: '100%', m: '75%', l: '50%' },
  display: 'block',
})``;

const Title = styled(Box).attrs({
  display: 'block',
  font: 'h2-semibold',
  mb: 2,
})``;

const Description = styled(Box).attrs({
  display: 'block',
  font: 'body-regular-asphalt',
  mb: { _: 3, m: 4 },
})``;

const WrapperPicture = styled(Box).attrs({
  height: { _: 91, m: 125, xl: 131 },
  width: { m: 226, l: 222, xl: 233 },
})``;
